import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import nookies, { parseCookies } from 'nookies'
import cogoToast from 'cogo-toast'
import ReCAPTCHA from 'react-google-recaptcha'
import * as Sentry from '@sentry/node'
import Loader from '@components/common/loader'

import { CClient } from '@common/utils'
import { verifyOtpAndLogin, requestLoginOtp } from '@containers/auth/utils'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import {
  buildIdentifierBody,
  detectIdentifierMode,
  OtpRequestBody,
} from '@lib/utils/identifier'

import VerifyPhoneOtp from '@components/auth/verify-otp'
import { RecaptchaType } from '@lib/types/recaptcha'
import { t } from '@lingui/macro'
import { MixpanelEvent, trackMixpanelEvent } from '@lib/analytics/mixpanel'

type Props = {
  phone: string
  redirectUrl?: string
  source: string | string[]
  handleOtpVerified?: () => Promise<any>
  // Which OTP page this is, sent with every Mixpanel event. Only the old
  // /register/details path uses registration; everything else is a login.
  flow?: 'registration' | 'login'
}

export const VerifyPhoneOtpContainer = (props: Props): JSX.Element => {
  const { redirectUrl, handleOtpVerified, flow = 'login' } = props
  const trackOtpEvent = (event: MixpanelEvent) =>
    trackMixpanelEvent(event, { flow })
  const TIMER_COUNT = 30
  const NUM_INPUTS = 6
  const recaptchaRef = useRef<RecaptchaType>(null)
  const [time, setTime] = useState(TIMER_COUNT)
  const [isTimerActive, setIsTimerActive] = useState(true)
  // const [showOtpViaPhoneOption, setShowOtpViaPhoneOption] = useState(false)
  const [isLoaderShown, setIsLoaderShown] = useState(false)
  const [
    isPerformingInitialLoginCheck,
    setIsPerformingInitialLoginCheck,
  ] = useState(true)
  const [OtpData, setOtpData] = useState({
    // User form inputs
    phone: props.phone,
    otp: '',

    // Server response
    response: {},
    errors: {},
    resendOtpLoading: false,
    willRequestOtpViaPhone: false,
    willRequestOtpViaWhatsapp: false,
    willRequestOtpViaEmail: false,
    willResendOtpViaSMS: false,
    sentVia: 'both',
  })
  const router = useRouter()

  const { phone, otp, resendOtpLoading, errors } = OtpData
  // `phone` carries whichever identifier the user logged in with. An email one
  // has no reachable number behind it, so the phone-only resend channels are
  // hidden and every request sends `email` instead of `phone`.
  const identifierMode = detectIdentifierMode(OtpData.phone)
  const isEmailIdentifier = identifierMode === 'email'

  const performLogin = async () => {
    setIsLoaderShown(true)

    handleOtpVerified && (await handleOtpVerified())

    const currentQuery = router.query
    delete currentQuery.next
    delete currentQuery.phone
    delete currentQuery.ph
    const query = { ...currentQuery, source: props.source }

    redirectUrl &&
      router.push({
        pathname: redirectUrl,
        query,
      })

    changeOtpData({
      ['errors']: {},
    })
  }

  useEffect(() => {
    const initialLogin = async () => {
      // The screen is only shown when the user is not already logged in.
      const trackVisit = () =>
        trackOtpEvent(
          flow === 'registration'
            ? MixpanelEvent.PHONE_VERIFY_VISIT_AFTER_REGISTRATION
            : MixpanelEvent.PHONE_VERIFY_VISIT
        )
      const { token } = parseCookies()
      if (token === undefined) {
        setIsPerformingInitialLoginCheck(false)
        trackVisit()
        return
      }

      const res = await CClient('/api/v1/users/me/', {
        method: 'GET',
        headers: { Authorization: `Token ${token}` },
      })
      if (res.ok) await performLogin()
      else {
        setIsPerformingInitialLoginCheck(false)
        trackVisit()
      }
    }
    initialLogin()
  }, [])

  useEffect(() => {
    // First digit typed
    if (OtpData.otp.length === 1)
      trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_ENTER)
    if (OtpData.otp.length === NUM_INPUTS) {
      handleOtpFormSubmit()
    }
    setOtpData({ ...OtpData, ['errors']: '' })
  }, [OtpData.otp])

  useEffect(() => {
    let interval = null
    if (time && isTimerActive) {
      interval = setInterval(() => {
        setTime((time) => time - 1)
      }, 1000)
    } else if (!time && isTimerActive) {
      clearInterval(interval)
      setIsTimerActive(false)
    }
    return () => clearInterval(interval)
  }, [isTimerActive, time])

  const resetTimer = () => {
    setTime(TIMER_COUNT)
    setIsTimerActive(true)
  }

  const changeOtpData = (data) => {
    setOtpData((prevState) => ({ ...prevState, ...data }))
  }

  const handleInputChange = async (otp) => {
    changeOtpData({ ['otp']: otp })
  }

  const handleOtpFormSubmit = async () => {
    setIsLoaderShown(true)

    const formBody = {
      ...buildIdentifierBody(OtpData.phone, identifierMode),
      token: OtpData.otp,
    }

    try {
      const result = await verifyOtpAndLogin(formBody)

      if (result.ok) {
        trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_SUCCESS)
        setIsLoaderShown(true)
        const resultData = await result.json()
        nookies.set({}, 'token', resultData['token'], {
          maxAge: 365 * 24 * 60 * 60,
          path: '/',
        })
        await performLogin()
      } else {
        trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_ERROR)
        changeOtpData({
          ['errors']: await result.json(),
        })
      }
      setIsLoaderShown(false)
    } catch (error) {
      Sentry.captureException(error)
      setIsLoaderShown(false)
    }
  }

  const handleResendOtpViaSMS = async () => {
    if (OtpData.resendOtpLoading) return
    trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_RESEND_SMS)

    changeOtpData({
      resendOtpLoading: true,
      willRequestOtpViaPhone: false,
      willRequestOtpViaWhatsapp: false,
      willRequestOtpViaEmail: false,
      willResendOtpViaSMS: true,
      sentVia: 'an SMS',
    })

    const formBody: OtpRequestBody = buildIdentifierBody(
      OtpData.phone,
      identifierMode
    )
    const _countryResendSms = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (_countryResendSms) formBody.country = _countryResendSms.toUpperCase()
    try {
      const result = await requestLoginOtp(formBody, false, false, false, true)
      if (result.ok) {
        changeOtpData({
          errors: {},
          resendOtpLoading: false,
        })

        cogoToast.success(t`You should receive an SMS with OTP soon!`, {
          hideAfter: 5,
        })
        // Reset timer after otp is received
        resetTimer()
      } else if (result.status === 429) {
        await recaptchaRef.current?.execute()
      } else {
        changeOtpData({
          errors: await result.json(),
          resendOtpLoading: false,
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      changeOtpData({ resendOtpLoading: false })
    }
  }

  const handleSendOtpViaWhatsapp = async () => {
    if (OtpData.resendOtpLoading) return
    trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_RESEND_WHATSAPP)

    changeOtpData({
      resendOtpLoading: true,
      willRequestOtpViaPhone: false,
      willRequestOtpViaWhatsapp: true,
      willRequestOtpViaEmail: false,
      willResendOtpViaSMS: false,
      sentVia: ' a Whatsapp Message',
    })

    const formBody: OtpRequestBody = buildIdentifierBody(
      OtpData.phone,
      identifierMode
    )
    const _countryResendWhatsapp = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (_countryResendWhatsapp)
      formBody.country = _countryResendWhatsapp.toUpperCase()
    try {
      const result = await requestLoginOtp(formBody, false, true)

      if (result.ok) {
        changeOtpData({
          errors: {},
          resendOtpLoading: false,
        })

        cogoToast.success(
          t`You should receive a Whatsapp Message with OTP soon!`,
          {
            hideAfter: 5,
          }
        )
        // Reset timer after otp is received
        resetTimer()
      } else if (result.status === 429) {
        await recaptchaRef.current?.execute()
      } else {
        changeOtpData({
          errors: await result.json(),
          resendOtpLoading: false,
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      changeOtpData({ resendOtpLoading: false })
    }
  }

  const handleSendOtpViaCall = async () => {
    if (OtpData.resendOtpLoading) return
    trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_RESEND_CALL)

    changeOtpData({
      resendOtpLoading: true,
      willRequestOtpViaPhone: true,
      willRequestOtpViaWhatsapp: false,
      willRequestOtpViaEmail: false,
      willResendOtpViaSMS: false,
      sentVia: 'a Phone Call',
    })

    const formBody: OtpRequestBody = buildIdentifierBody(
      OtpData.phone,
      identifierMode
    )
    const _countryResendCall = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (_countryResendCall) formBody.country = _countryResendCall.toUpperCase()
    try {
      const response = await requestLoginOtp(formBody, true)

      if (response.ok) {
        changeOtpData({
          errors: {},
          resendOtpLoading: false,
        })
        cogoToast.success(t`You should receive a phone call soon!`, {
          hideAfter: 5,
        })

        // Reset timer after otp is received
        resetTimer()
      } else if (response.status === 429) await recaptchaRef.current?.execute()
      else {
        changeOtpData({
          errors: await response.json(),
          resendOtpLoading: false,
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      changeOtpData({ resendOtpLoading: false })
    }
  }
  const handleResendOtpViaEmail = async () => {
    if (OtpData.resendOtpLoading) return
    trackOtpEvent(MixpanelEvent.PHONE_VERIFY_OTP_RESEND_EMAIL)

    changeOtpData({
      resendOtpLoading: true,
      willRequestOtpViaPhone: false,
      willRequestOtpViaWhatsapp: false,
      willRequestOtpViaEmail: true,
      willResendOtpViaSMS: false,
      sentVia: 'an E-mail',
    })

    const formBody: OtpRequestBody = buildIdentifierBody(
      OtpData.phone,
      identifierMode
    )
    const _countryResendEmail = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (_countryResendEmail)
      formBody.country = _countryResendEmail.toUpperCase()

    try {
      const response = await requestLoginOtp(formBody, false, false, true)

      if (response.ok) {
        changeOtpData({
          errors: {},
          resendOtpLoading: false,
        })
        cogoToast.success(t`You should receive an e-mail soon!`, {
          hideAfter: 5,
        })

        // Reset timer after otp is received
        resetTimer()
      } else if (response.status === 429) await recaptchaRef.current?.execute()
      else {
        changeOtpData({
          errors: await response.json(),
          resendOtpLoading: false,
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      changeOtpData({ resendOtpLoading: false })
    }
  }
  const handleCaptcha = async (key) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!key) return

    // Else reCAPTCHA was executed successfully so proceed with requesting OTP
    const formBody: OtpRequestBody & { recaptchaResponse: string } = {
      ...buildIdentifierBody(OtpData.phone, identifierMode),
      recaptchaResponse: key,
    }
    const _countryResendCaptcha = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    if (_countryResendCaptcha)
      formBody.country = _countryResendCaptcha.toUpperCase()

    recaptchaRef.current?.reset()

    try {
      const response = await requestLoginOtp(
        formBody,
        OtpData.willRequestOtpViaPhone,
        OtpData.willRequestOtpViaWhatsapp
      )
      if (response.ok) {
        if (OtpData.willRequestOtpViaPhone) {
          cogoToast.success(t`You should receive a phone call soon!`, {
            hideAfter: 5,
          })

          changeOtpData({
            errors: {},
            resendOtpLoading: false,
          })
          // Reset timer after otp is received
          resetTimer()
        } else if (OtpData.willRequestOtpViaWhatsapp) {
          cogoToast.success(
            t`You should receive a whatsapp message with OTP soon!`,
            {
              hideAfter: 5,
            }
          )

          changeOtpData({
            errors: {},
            resendOtpLoading: false,
          })
          // Reset timer after otp is received
          resetTimer()
        } else {
          cogoToast.success(t`You should receive an SMS with OTP soon!`, {
            hideAfter: 5,
          })

          changeOtpData({
            errors: {},
            resendOtpLoading: false,
          })
          // Reset timer after otp is received
          resetTimer()
        }
      } else if (response.status === 406) {
        changeOtpData({
          errors: { token: [t`Error verifying reCAPTCHA, please try again`] },
          resendOtpLoading: false,
        })
      } else {
        changeOtpData({
          errors: await response.json(),
          resendOtpLoading: false,
        })
        throw new Error(response.statusText)
      }
    } catch (error) {
      Sentry.captureException(error)
      changeOtpData({
        resendOtpLoading: false,
      })
    }
  }

  return (
    <div data-testid="verify-otp">
      {isPerformingInitialLoginCheck && (
        <Loader classes="h-6 w-6 mx-auto my-4" />
      )}

      {!isPerformingInitialLoginCheck && (
        <>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            onChange={handleCaptcha}
          />
          <VerifyPhoneOtp
            phone={phone}
            isEmailIdentifier={isEmailIdentifier}
            otp={otp}
            resendOtpLoading={resendOtpLoading}
            errors={errors}
            handleInputChange={handleInputChange}
            handlePhoneVerificationFormSubmit={handleOtpFormSubmit}
            handleResendOtpViaSMS={handleResendOtpViaSMS}
            handleResendOtpViaEmail={handleResendOtpViaEmail}
            timer={time}
            timerActive={isTimerActive}
            handleSendOtpViaWhatsapp={handleSendOtpViaWhatsapp}
            handleSendOtpViaCall={handleSendOtpViaCall}
            isLoaderShown={isLoaderShown}
            numInputs={NUM_INPUTS}
            sentVia={OtpData.sentVia}
          />
        </>
      )}
    </div>
  )
}
